import { useContext, useEffect } from 'preact/hooks';
import { createRef } from 'preact';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { ShareButton } from '@playbook/sdk-helpers';

import styleSheet from './poll.module.scss';

import { enableEditableContent } from '../../helpers/editable-content';
import { PollContext } from '../../potg/potg.component';

const styles = styleSheet.locals || {};

/**
 * Component for the poll header
 *
 * @param {object} props poll option props
 * @param {object} props.poll poll option data
 * @param {boolean} props.pollScheduled if poll is scheduled
 * @param {boolean} props.showVoteCastScreen if vote cast screen is shown (results hidden)
 * @param {boolean} props.showPollOptions if poll options are shown
 * @returns {Function} <PollHeader />
 */
function PollHeader({
    poll,
    pollScheduled = false,
    showVoteCastScreen = false,
    showPollOptions = false
}) {
    const { t } = useTranslation();
    const { isPreview, onPreviewEditChange, pollUserAnswer, interactionEvent } =
        useContext(PollContext);
    const question = poll.questions[0];
    const editableAttr = isPreview ? { contentEditable: true } : {};
    const titleRef = createRef();
    const descrRef = createRef();

    const changeEditableData = (field, value) => {
        onPreviewEditChange?.({
            [field]: value,
            questions: [
                {
                    [field]: value,
                    options: []
                }
            ]
        });
    };

    useEffect(() => {
        if (isPreview) {
            enableEditableContent(titleRef.current, 'text', changeEditableData);
            enableEditableContent(
                descrRef.current,
                'description',
                changeEditableData
            );
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <div
            part="potg-header"
            className={classNames(styles.pollHeader, {
                [styles.pollHeaderScheduled]: pollScheduled,
                [styles.pollHeaderCentered]:
                    showVoteCastScreen && !pollUserAnswer && !showPollOptions
            })}
        >
            <div className={classNames(styles.pollPrompt)}>
                <span
                    part="potg-question"
                    className={classNames(styles.pollQuestion)}
                    ref={titleRef}
                    data-fek-preview="text"
                    {...editableAttr}
                >
                    {question.text}
                </span>
                <div
                    part="potg-description"
                    className={classNames(styles.pollDescription)}
                    ref={descrRef}
                    data-fek-preview="description"
                    {...editableAttr}
                >
                    {question.description}
                </div>
            </div>

            {!pollScheduled && (
                <ShareButton
                    pageId={poll.poll_id}
                    copiedLabel={t('button.shareCopied')}
                    ariaLabel={t('button.sharePoll')}
                    partPrefix="potg-share"
                    onCopied={() => interactionEvent?.send('POLL_SHARED')}
                />
            )}
        </div>
    );
}

PollHeader.propTypes = {
    poll: PropTypes.object.isRequired,
    pollScheduled: PropTypes.bool,
    showVoteCastScreen: PropTypes.bool,
    showPollOptions: PropTypes.bool
};

export default PollHeader;
